import { useState } from "react";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/styles";
import { FormControlLabel, Checkbox, Snackbar, Divider } from "@material-ui/core";
import { Alert } from "@material-ui/lab";

import uiConf from "../../config/uiConf";

import apiClient from "../../lib/common/apiClient";
import checkValidation from "../../lib/utils/checkValidation";
import checkConditionWithMsg from "../../lib/utils/checkConditionWithMsg";

import Dialog from "../dialog/functional/Dialog";
import DialogHeader from "../dialog/functional/DialogHeader";
import DialogContent from "../dialog/functional/DialogContent";
import DialogTitle from "../dialog/functional/DialogTitle";
import DialogActionsOkCancel from "../dialog/functional/DialogActionsOkCancel";
import AutoForm from "../form/functional/AutoForm";
import failureKey from "../../lib/common/failureKey";

const useStyles = makeStyles((theme) => ({
    keepOpenSwitch: {
        marginLeft: "8px",
    },
    label: {
        fontSize: "smaller",
        color: theme.palette.primary.text,
    },
}));
/**
 * @COMPONENT_NAME MaterialUnitGroupDetailCreateDialog
 * @DESCRIPTION 자재 단위 그룹 상세 생성을 위한 다이얼로그 컴포넌트
 *             좌측에 자재 단위 목록을 표시하고 우측에 자재 단위 그룹 상세 생성 폼을 제공
 *             자재 단위를 선택하면 해당 단위를 포함하여 단위 그룹 상세를 생성
 *
 * @PROPS
 *   - {string} id - 다이얼로그 ID
 *   - {Object} schema - 자재 단위 그룹 상세 테이블 스키마 정보
 *   - {Object} selectedMst - 선택된 자재 단위 그룹 정보
 *   - {boolean} moveable - 다이얼로그 이동 가능 여부
 *   - {boolean} resizable - 다이얼로그 크기 조정 가능 여부
 *   - {boolean} fullWidth - 전체 너비 사용 여부
 *   - {string} maxWidth - 최대 너비 설정
 *   - {string} title - 다이얼로그 제목
 *   - {Function} onClose - 다이얼로그 닫기 시 호출되는 콜백
 *   - {Function} onInitialized - 다이얼로그 초기화 시 호출되는 콜백
 *   - {Function} onSuccess - 생성 성공 시 호출되는 콜백
 *   - {Function} customizeErrorMsgCallback - 에러 메시지 커스터마이징 콜백
 *
 * @STATE
 *   - {Object} dialogInstance - 다이얼로그 인스턴스 참조
 *   - {Object} formData - 폼 데이터
 *   - {boolean} okButtonDisabled - 확인 버튼 비활성화 상태
 *   - {boolean} keepOpen - 다이얼로그 계속 열어두기 상태
 *   - {Object} notification - 알림 상태
 *
 * @CALLBACKS
 *   - {Function} onDialogInitialized - 다이얼로그 초기화 시 호출되는 콜백
 *   - {Function} onApiSuccess - API 성공 시 호출되는 콜백
 *   - {Function} onApiFailure - API 실패 시 호출되는 콜백
 *   - {Function} onOk - 확인 버튼 클릭 시 호출되는 콜백
 *   - {Function} onCancel - 취소 버튼 클릭 시 호출되는 콜백
 *   - {Function} onChangeFormData - 폼 데이터 변경 시 호출되는 콜백
 *   - {Function} onCloseDialog - 다이얼로그 닫기 시 호출되는 콜백
 *   - {Function} checkItemValidation - 폼 유효성 검사 함수
 */

function MaterialUnitGroupDetailCreateDialog(props) {
    const {
        id,
        schema,
        selectedMst,

        platform,
        moveable,
        resizable,
        fullWidth,
        maxWidth,

        onClose,
        onInitialized,
        onSuccess,

        title,

        customizeErrorMsgCallback,

        ...others
    } = props;

    const classes = useStyles();
    const { t } = useTranslation();

    const [dialogInstance, setDialogInstance] = useState();
    const [formData, setFormData] = useState();

    const [okButtonDisabled, setOkButtonDisabled] = useState(true);
    const [keepOpen, setKeepOpen] = useState(false);

    const [notification, setNotification] = useState({
        open: false,
        severity: "success",
        msg: "",
    });

    const notifySuccess = (msg) => {
        setNotification({
            severity: "success",
            msg: msg,
            open: true,
        });
    };

    const notifyFailure = (msg) => {
        setNotification({
            severity: "error",
            msg: msg,
            open: true,
        });
    };

    const notifyWarning = (msg) => {
        setNotification({
            severity: "warning",
            msg: msg,
            open: true,
        });
    };

    const onDialogInitialized = (instance) => {
        setDialogInstance(instance);

        if (onInitialized) {
            onInitialized(instance);
        }
    };

    const onApiSuccess = (result) => {
        setKeepOpen((keepOpen) => {
            if (!keepOpen) {
                dialogInstance.hide();
            } else {
                setFormData();
                setOkButtonDisabled(true);
            }

            if (onSuccess) {
                setTimeout(() => onSuccess(t("success.insertSuccess", { table: schema.name })), 0);
            } else {
                notifySuccess(t("success.insertSuccess", { table: schema.name }));
            }
            return keepOpen;
        });
    };

    const onApiFailure = (reason) => {
        if (customizeErrorMsgCallback) {
            notifyFailure(customizeErrorMsgCallback(reason));
        } else {
            notifyFailure(
                t(failureKey("insert", reason.response?.status), { reason: reason.response ? reason.response.data : JSON.stringify(reason) })
            );
        }
    };

    const onOk = (schema, formData) => {
        if (!checkItemValidation(schema, formData)) {
            console.error("Form Validation Failed");
            return;
        }

        if (schema.validation) {
            let [valid, msg] = checkConditionWithMsg(formData, schema.validation);

            if (!valid) {
                notifyWarning(t("warning.formDataValidationFail", { msg: msg }));
                return;
            }
        }

        // build base api url
        let url = `/api/${schema.name}`;

        let apiData = { ...formData, unitGroupId: selectedMst?.unitGroupId };

        let form = new FormData();
        let json = {};
        let deleted = {};

        for (const formItem in apiData) {
            if (schema.columnMap && schema.columnMap[formItem] && schema.columnMap[formItem].formItemType === "file") {
                deleted[formItem] = apiData[formItem].deletedFiles;

                for (const file of apiData[formItem].activeFiles) {
                    if (file.type === "url") continue;
                    form.append(formItem, file.file);
                }
            } else {
                if (apiData[formItem] !== undefined) {
                    json[formItem] = apiData[formItem];
                }
            }
        }

        form.append("deleted", JSON.stringify(deleted));
        form.append("json", JSON.stringify(json));

        apiClient
            .post(url, form, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            })
            .then((result) => onApiSuccess(result))
            .catch((reason) => onApiFailure(reason));
    };

    const onCancel = () => {
        setOkButtonDisabled(true);
        dialogInstance.hide();
    };

    const checkKeepOpen = () => {
        return (
            <>
                <FormControlLabel
                    control={
                        <Checkbox
                            className={classes.keepOpenSwitch}
                            checked={keepOpen}
                            color="primary"
                            onChange={() => setKeepOpen((keepOpen) => !keepOpen)}
                            name="checkKeepOpen"
                            size="small"
                        />
                    }
                    label={<span className={classes.label}>{t("term.keepDialogOpen")}</span>}
                />
                <div style={{ flexGrow: 1 }}></div>
            </>
        );
    };

    const onChangeFormData = (formData) => {
        setFormData(formData);
        setOkButtonDisabled(false);
    };

    const onCloseNotification = () => {
        setNotification((notification) => {
            return { ...notification, open: false };
        });
    };

    const onCloseDialog = () => {
        setOkButtonDisabled(true);
        setFormData({ unitGroupId: selectedMst?.unitGroupId });

        if (onClose) {
            onClose();
        }
    };

    const checkItemValidation = (schema, formData) => {
        for (const column of schema.columns) {
            let [valid, msg] = checkValidation("create", column, column.validation, formData[column.name] || "");

            if (!valid) {
                notifyWarning(msg);
                return false;
            }
        }

        return true;
    };

    return (
        <Dialog
            id={id}
            platform={platform ? platform : uiConf.dialogPlatform}
            moveable={moveable ? moveable : uiConf.dialogMoveable}
            resizable={resizable ? resizable : uiConf.dialogResizable}
            fullWidth={fullWidth ? fullWidth : uiConf.dialogFullWidth}
            maxWidth={maxWidth ? maxWidth : uiConf.autoFormDialogMaxWidth}
            onClose={onCloseDialog}
            onInitialized={onDialogInitialized}
            {...others}
        >
            <DialogHeader>
                <DialogTitle>{title}</DialogTitle>
            </DialogHeader>

            <Divider />

            <DialogContent dividers>
                <AutoForm
                    id="material-unit-group-detail-create-form"
                    schema={schema}
                    mode="create"
                    initialData={formData}
                    onChange={onChangeFormData}
                />

                <Snackbar
                    open={notification.open && notification.severity !== "success"}
                    autoHideDuration={uiConf.dialogNotifyAutoHideDuration}
                    onClose={onCloseNotification}
                >
                    <Alert onClose={onCloseNotification} variant="filled" severity={notification.severity}>
                        {notification.msg}
                    </Alert>
                </Snackbar>
            </DialogContent>

            <DialogActionsOkCancel
                firstActionComponent={checkKeepOpen}
                labels={[t("common.add")]}
                buttonDisabled={[okButtonDisabled, false]} /* [okButtonDisabled, cancelButtonDisabled] */
                onOk={() => onOk(schema, formData)}
                onCancel={onCancel}
            />
        </Dialog>
    );
}

export default MaterialUnitGroupDetailCreateDialog;
